import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Grid,
    LinearProgress,
    Typography
} from '@mui/material';
import {
    GoogleMap,
    InfoWindow,
    Marker
} from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import CircularLoading from "../components/CircularLoading";
import BookingDataLoadError from "../components/BookingDataLoadError";
import {
    ActionButton,
    EmptyState,
    PageHeader,
    SectionHeader,
    StatusBadge,
    SurfaceCard
} from '../components/adminPrimitives';
import AdminStatusBadge from '../components/AdminStatus';
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';
import { api } from 'common';
import adminTokens from '../styles/adminDesignTokens';
import {
    FONT_FAMILY,
    calcEst,
    showEst,
    optionsRequired,
    formatCurrency,
} from 'common/sharedFunctions';
import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import RouteIcon from '@mui/icons-material/Route';
import TodayIcon from '@mui/icons-material/Today';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ViewListIcon from '@mui/icons-material/ViewList';

const matchesFleetOwner = (entity, uid) => !!(
    entity && uid && (
        entity.fleetadmin === uid ||
        entity.fleetadminUid === uid ||
        entity.fleetAdminUid === uid ||
        entity.createdBy === uid ||
        entity.createdByUid === uid ||
        (entity.fleetadmins && entity.fleetadmins[uid])
    )
);

const amountOrZero = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
};

const normalizeStatus = (status) => String(status || "UNKNOWN").trim().toUpperCase();

const getBookingDate = (booking = {}) => {
    const value = booking.tripdate || booking.bookingDate || booking.createdAt || booking.created_at || booking.orderDate;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getBookingReference = (booking = {}) => (
    booking.reference ||
    booking.bookingRef ||
    booking.bookingId ||
    booking.id ||
    booking.uid ||
    "-"
);

const getBookingClient = (booking = {}) => (
    booking.customer_name ||
    booking.customerName ||
    booking.clientName ||
    booking.client_name ||
    booking.customer ||
    booking.companyName ||
    "-"
);

const getBookingPickup = (booking = {}) => (
    booking.pickupAddress ||
    booking.pickup_address ||
    booking.pickup ||
    booking.pickupLocation ||
    booking.pickup?.add ||
    booking.pickup?.address ||
    "-"
);

const getBookingDrop = (booking = {}) => (
    booking.dropAddress ||
    booking.drop_address ||
    booking.destination ||
    booking.drop ||
    booking.dropLocation ||
    booking.drop?.add ||
    booking.drop?.address ||
    "-"
);

const getShipmentType = (booking = {}) => (
    booking.serviceType ||
    booking.shipmentType ||
    booking.cargoType ||
    booking.carType ||
    booking.vehicleType ||
    "-"
);

const isCompletedStatus = (status) => status === "PAID" || status === "COMPLETE" || status === "COMPLETED";
const isPendingStatus = (status) => ["NEW", "PENDING", "ACCEPTED", "REQUESTED", "PAYMENT_PENDING"].includes(status);
const isActiveStatus = (status) => ["STARTED", "ARRIVED", "REACHED", "ON_TRIP", "IN_PROGRESS"].includes(status);
const isCancelledStatus = (status) => ["CANCELLED", "CANCELED"].includes(status);
const hasEcrSignal = (booking = {}) => !!(
    booking.emptyContainerReturn ||
    booking.emptyContainerReturnStatus ||
    booking.emptyContainerReturnProofs ||
    booking.containerReturn ||
    booking.containerReturnStatus ||
    booking.ecr ||
    booking.ecrStatus
);

const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
};

const formatDate = (date) => new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric"
}).format(date);

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);
    const [settings, setSettings] = useState({});
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const navigate = useNavigate();
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const dispatch = useDispatch();
    const {fetchDrivers, clearFetchDrivers} = api;
    const profile = useMemo(() => auth.profile || {}, [auth.profile]);
    const userRole = profile.usertype;

    useEffect(() => {
        dispatch(fetchDrivers('web'));
        return () => {
            dispatch(clearFetchDrivers());
        };
    }, [dispatch, fetchDrivers, clearFetchDrivers]);

    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                position => setMylocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }),
                error => console.log(error)
            );
        }
    }, [mylocation]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (usersdata.drivers && bookinglistdata.bookings) {
            const liveBookings = bookinglistdata.bookings.filter(bkg => bkg.status === 'STARTED');
            const drivers = usersdata.drivers;
            let locs = [];

            for (let i = 0; i < drivers.length; i++) {
                if (drivers[i].location) {
                    let carImage = 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png';
                    let bookingRef = null;
                    for (let j = 0; j < cars.length; j++) {
                        if (cars[j].name === drivers[i].carType) {
                            carImage = cars[j].image;
                        }
                    }
                    for(let j = 0; j<liveBookings.length; j++){
                        if(liveBookings[j].driver === drivers[i].uid || liveBookings[j].assignedDriverUid === drivers[i].uid){
                            bookingRef = liveBookings[j].reference;
                        }
                    }
                    locs.push({
                        id: i,
                        lat: drivers[i].location.lat,
                        lng: drivers[i].location.lng,
                        drivername: `${drivers[i].firstName || ""} ${drivers[i].lastName || ""}`.trim() || "Driver",
                        carnumber: drivers[i].vehicleNumber,
                        cartype: drivers[i].carType,
                        carImage: carImage,
                        bookingRef: bookingRef,
                        fleetadmin: drivers[i].fleetadmin || drivers[i].fleetadminUid || drivers[i].fleetAdminUid || drivers[i].createdBy || drivers[i].createdByUid
                    });
                }
            }
            if (userRole === 'fleetadmin') {
                locs = locs.filter(driver => driver.fleetadmin === profile.uid);
            }
            setLocations(locs);
        }
    }, [usersdata.drivers, userRole, profile.uid, cars, bookinglistdata.bookings, settings]);

    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                const booking = bookinglistdata.bookings[i];
                const ownerVisible = userRole === 'admin' || (userRole === 'fleetadmin' && matchesFleetOwner(booking, profile.uid));
                if ((booking.status === 'PAID' || booking.status === 'COMPLETE') && ownerVisible) {
                    const tDate = getBookingDate(booking);
                    if (!tDate) continue;
                    let earningAmount = 0;
                    if (userRole === 'admin') {
                        const explicitCommission = booking.superAdminCommissionAmount ?? booking.garahexCommissionAmount;
                        earningAmount = explicitCommission === undefined || explicitCommission === null
                            ? amountOrZero(booking.convenience_fees) - amountOrZero(booking.discount)
                            : amountOrZero(explicitCommission);
                    } else {
                        earningAmount = amountOrZero(
                            booking.fleetAdminNetAmount ?? booking.fleetAdminEarningNetAmount ?? booking.fleetCommission
                        );
                    }
                    if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                        todayTotal += earningAmount;
                    }
                    if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                        monthlyTotal += earningAmount;
                    }
                    yearlyTotal += earningAmount;
                }
            }
            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
        }
    }, [bookinglistdata.bookings, settings, profile.uid, userRole]);

    useEffect(() => {
        if (usersdata.users) {
            let driverCount = 0;
            let activeCount = 0;
            let customerCount = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                if (usersdata.users[i].usertype === 'driver') {
                    if ((userRole === 'fleetadmin' && matchesFleetOwner(usersdata.users[i], profile.uid)) || userRole === 'admin') {
                        driverCount = driverCount + 1;
                    }
                }
                if (usersdata.users[i].driverActiveStatus === true) {
                    if ((userRole === 'fleetadmin' && matchesFleetOwner(usersdata.users[i], profile.uid)) || userRole === 'admin') {
                        activeCount = activeCount + 1;
                    }
                }
                if (usersdata.users[i].usertype === 'customer') {
                    if ((userRole === 'fleetadmin' && matchesFleetOwner(usersdata.users[i], profile.uid)) || userRole === 'admin') {
                        customerCount = customerCount + 1;
                    }
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
        } else {
            setActiveCount(0);
            setDriverCount(0);
            setCustomerCount(0);
        }
    }, [usersdata.users, profile, userRole]);

    useEffect(() => {
        let completeCounts = [];
        let cancelCounts = [];
        let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        if (bookinglistdata.bookings) {
            for (let i = 0; i < months.length; i++) {
                let completeCount = 0;
                let cancelCount = 0;
                for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                    const tDate = getBookingDate(bookinglistdata.bookings[j]);
                    if (!tDate) continue;
                    const status = normalizeStatus(bookinglistdata.bookings[j].status);
                    if (months[i] === months[tDate.getMonth()] && isCompletedStatus(status)) {
                        completeCount = completeCount + 1;
                    }
                    if (months[i] === months[tDate.getMonth()] && isCancelledStatus(status)) {
                        cancelCount = cancelCount + 1;
                    }
                }
                completeCounts.push(completeCount);
                cancelCounts.push(cancelCount);
            }
        }
        setAllCompleteCount(completeCounts);
        setAllCancelCount(cancelCounts);
    }, [bookinglistdata.bookings]);

    const visibleBookings = useMemo(() => {
        const bookings = bookinglistdata.bookings || [];
        if (userRole !== "fleetadmin") return bookings;
        return bookings.filter((booking) => matchesFleetOwner(booking, profile.uid));
    }, [bookinglistdata.bookings, profile.uid, userRole]);

    const dashboardMetrics = useMemo(() => {
        const activeBookings = visibleBookings.filter((booking) => isActiveStatus(normalizeStatus(booking.status))).length;
        const pendingBookings = visibleBookings.filter((booking) => isPendingStatus(normalizeStatus(booking.status))).length;
        const completedBookings = visibleBookings.filter((booking) => isCompletedStatus(normalizeStatus(booking.status))).length;
        const cancelledBookings = visibleBookings.filter((booking) => isCancelledStatus(normalizeStatus(booking.status))).length;
        const ecrBookings = visibleBookings.filter(hasEcrSignal).length;
        const pendingBidStatuses = ["BIDDED", "BID_REQUESTED", "BID_PENDING", "WAITING_FOR_BID", "QUOTE_REQUESTED"];
        const pendingBids = visibleBookings.filter((booking) => pendingBidStatuses.includes(normalizeStatus(booking.status)) || booking.bidStatus === "pending").length;

        return {
            activeBookings,
            pendingBookings,
            completedBookings,
            cancelledBookings,
            ecrBookings,
            pendingBids
        };
    }, [visibleBookings]);

    const recentBookings = useMemo(() => {
        return [...visibleBookings]
            .sort((a, b) => {
                const aTime = getBookingDate(a)?.getTime() || 0;
                const bTime = getBookingDate(b)?.getTime() || 0;
                return bTime - aTime;
            })
            .slice(0, 6);
    }, [visibleBookings]);

    const profileName = useMemo(() => {
        const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim();
        return profile.companyName || profile.businessName || profile.name || fullName || profile.email || "Admin";
    }, [profile]);

    const quickActions = useMemo(() => {
        const actions = [];
        const addBookingAccess = calcEst && !showEst && !optionsRequired ? ["customer"] : ["admin", "fleetadmin", "customer"];
        if (addBookingAccess.includes(userRole)) {
            actions.push({ label: "New Booking", path: "/addbookings", icon: <AddIcon /> });
        }
        if (["admin", "fleetadmin"].includes(userRole)) {
            actions.push({ label: "Add Client", path: "/users/edituser/customer", icon: <GroupsIcon /> });
            actions.push({ label: "Add Driver", path: "/users/edituser/driver", icon: <EmojiPeopleIcon /> });
        }
        if (["admin", "fleetadmin", "driver"].includes(userRole)) {
            actions.push({ label: "Add Vehicle", path: "/cars/editcar", icon: <DirectionsCarIcon /> });
        }
        return actions;
    }, [userRole]);

    const kpiCards = useMemo(() => ([
        {
            label: "Total Bookings",
            value: visibleBookings.length,
            detail: `${dashboardMetrics.activeBookings} active now`,
            icon: <ViewListIcon />,
            tone: adminTokens.colors.electricBlue
        },
        {
            label: "Active Bookings",
            value: dashboardMetrics.activeBookings,
            detail: "Started or in progress",
            icon: <RouteIcon />,
            tone: adminTokens.colors.garahexOrange
        },
        {
            label: "Completed Deliveries",
            value: dashboardMetrics.completedBookings,
            detail: `${dashboardMetrics.cancelledBookings} cancelled`,
            icon: <CheckCircleIcon />,
            tone: adminTokens.colors.success
        },
        {
            label: "Pending Bookings",
            value: dashboardMetrics.pendingBookings,
            detail: "Awaiting next action",
            icon: <PendingActionsIcon />,
            tone: adminTokens.colors.warning
        },
        {
            label: "Clients",
            value: customerCount,
            detail: "Registered customers",
            icon: <GroupsIcon />,
            tone: adminTokens.colors.purple
        },
        {
            label: "Drivers",
            value: driverCount,
            detail: `${activeCount} active drivers`,
            icon: <EmojiPeopleIcon />,
            tone: adminTokens.colors.cyan
        }
    ]), [activeCount, customerCount, dashboardMetrics, driverCount, visibleBookings.length]);

    const statusOverview = useMemo(() => {
        const total = visibleBookings.length || 1;
        return [
            { label: "Active", value: dashboardMetrics.activeBookings, tone: adminTokens.colors.garahexOrange },
            { label: "Pending", value: dashboardMetrics.pendingBookings, tone: adminTokens.colors.warning },
            { label: "Complete", value: dashboardMetrics.completedBookings, tone: adminTokens.colors.success },
            { label: "Cancelled", value: dashboardMetrics.cancelledBookings, tone: adminTokens.colors.danger }
        ].map((item) => ({
            ...item,
            percent: Math.round((item.value / total) * 100)
        }));
    }, [dashboardMetrics, visibleBookings.length]);

    const needsAttention = useMemo(() => {
        const items = [];
        if (dashboardMetrics.pendingBookings > 0) {
            items.push({
                label: "Pending bookings",
                value: dashboardMetrics.pendingBookings,
                route: "/bookings",
                tone: "warning"
            });
        }
        if (dashboardMetrics.pendingBids > 0 && userRole === "admin") {
            items.push({
                label: "Bids waiting for review",
                value: dashboardMetrics.pendingBids,
                route: "/admin-bid-monitoring",
                tone: "purple"
            });
        }
        if (dashboardMetrics.ecrBookings > 0) {
            items.push({
                label: "Container return records",
                value: dashboardMetrics.ecrBookings,
                route: "/empty-container-returns",
                tone: "info"
            });
        }
        return items;
    }, [dashboardMetrics, userRole]);

    const barChartOptions = {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        colors: [adminTokens.colors.success, adminTokens.colors.danger],
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'smooth',
            width: 3
        },
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 0.35,
                opacityFrom: 0.28,
                opacityTo: 0.04,
                stops: [0, 90, 100]
            }
        },
        grid: {
            borderColor: "rgba(8, 34, 61, 0.08)",
            strokeDashArray: 4
        },
        chart: {
            height: 350,
            type: 'area',
            fontFamily: FONT_FAMILY,
            toolbar: { show: false }
        },
        xaxis: {
            labels: {
                style: { fontFamily: FONT_FAMILY, colors: adminTokens.colors.muted }
            }
        },
        yaxis: {
            labels: {
                style: { fontFamily: FONT_FAMILY, colors: adminTokens.colors.muted }
            }
        },
        tooltip: {
            style: { fontFamily: FONT_FAMILY }
        },
        legend: {
            fontFamily: FONT_FAMILY,
            position: "top",
            horizontalAlign: "right"
        }
    };

    const barChart = [{
        name: t('completed_bookings'),
        data: allCompleteCount
    }, {
        name: t('cancelled_bookings'),
        data: allCancelCount
    }];

    if (bookinglistdata.error && bookinglistdata.error.flag) {
        return <BookingDataLoadError message={bookinglistdata.error.msg} />;
    }

    if (bookinglistdata.loading || usersdata.loading) {
        return <CircularLoading />;
    }

    return (
        <Box
            className="garahex-dashboard"
            sx={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}
        >
            <PageHeader
                eyebrow="Operations Command Center"
                title={`${getGreeting()}, ${profileName}`}
                description="Here’s what’s happening with Garahex operations today."
                actions={
                    <>
                        <StatusBadge tone="info">{formatDate(new Date())}</StatusBadge>
                        <StatusBadge tone="purple">{userRole || "admin"}</StatusBadge>
                    </>
                }
            />

            <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
                <Grid item xs={12} lg={8}>
                    <SurfaceCard
                        sx={{
                            minHeight: "100%",
                            background: `linear-gradient(135deg, ${adminTokens.colors.midnight}, ${adminTokens.colors.deepNavy})`,
                            color: "#fff",
                            overflow: "hidden",
                            position: "relative",
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                right: -80,
                                top: -90,
                                width: 260,
                                height: 260,
                                borderRadius: "50%",
                                background: "rgba(255, 107, 0, 0.18)"
                            }
                        }}
                    >
                        <Box sx={{ position: "relative", zIndex: 1 }}>
                            <SectionHeader
                                title="Gross Earnings"
                                description="Calculated from the booking values already loaded in this dashboard."
                                actions={<TrendingUpIcon sx={{ color: "#8bdcff" }} />}
                            />
                            <Grid container spacing={2}>
                                {[
                                    { label: t('today_text'), value: formatCurrency(dailygross, settings), icon: <TodayIcon />, tone: adminTokens.colors.garahexOrange },
                                    { label: t('this_month_text'), value: formatCurrency(monthlygross, settings), icon: <MonetizationOnIcon />, tone: adminTokens.colors.electricBlue },
                                    { label: t('total'), value: formatCurrency(totalgross, settings), icon: <AssessmentIcon />, tone: adminTokens.colors.success }
                                ].map((item) => (
                                    <Grid item xs={12} sm={4} key={item.label}>
                                        <Box
                                            sx={{
                                                p: 2.25,
                                                minHeight: 132,
                                                borderRadius: 4,
                                                background: "rgba(255,255,255,0.09)",
                                                border: "1px solid rgba(255,255,255,0.12)",
                                                transition: "transform 170ms cubic-bezier(.2,.8,.2,1), background 170ms cubic-bezier(.2,.8,.2,1)",
                                                "&:hover": {
                                                    transform: "translateY(-3px)",
                                                    background: "rgba(255,255,255,0.13)"
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: "inline-flex", p: 1, mb: 1.5, borderRadius: 3, bgcolor: `${item.tone}22`, color: item.tone }}>
                                                {item.icon}
                                            </Box>
                                            <Typography sx={{ color: "rgba(255,255,255,0.72)", fontWeight: 800, fontSize: "0.82rem" }}>{item.label}</Typography>
                                            <Typography sx={{ mt: 0.6, fontSize: "clamp(1.35rem,2.2vw,2rem)", fontWeight: 900 }}>{item.value}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </SurfaceCard>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <SurfaceCard sx={{ height: "100%" }}>
                        <SectionHeader title="Quick Actions" description="Shortcuts to existing admin workflows." />
                        <Grid container spacing={1.2}>
                            {quickActions.map((action) => (
                                <Grid item xs={12} sm={6} lg={12} key={action.label}>
                                    <ActionButton
                                        fullWidth
                                        startIcon={action.icon}
                                        onClick={() => navigate(action.path)}
                                        sx={{ justifyContent: "flex-start", py: 1.25 }}
                                    >
                                        {action.label}
                                    </ActionButton>
                                </Grid>
                            ))}
                        </Grid>
                    </SurfaceCard>
                </Grid>
            </Grid>

            <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
                {kpiCards.map((card) => (
                    <Grid item xs={12} sm={6} lg={4} xl={2} key={card.label}>
                        <SurfaceCard
                            sx={{
                                minHeight: 156,
                                position: "relative",
                                overflow: "hidden",
                                transition: "transform 170ms cubic-bezier(.2,.8,.2,1), box-shadow 170ms cubic-bezier(.2,.8,.2,1)",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: adminTokens.shadows.elevated
                                },
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    inset: "0 auto 0 0",
                                    width: 5,
                                    background: card.tone
                                }
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                                <Box>
                                    <Typography sx={{ color: adminTokens.colors.muted, fontSize: "0.82rem", fontWeight: 850 }}>{card.label}</Typography>
                                    <Typography sx={{ mt: 1, fontSize: "2.05rem", fontWeight: 950, color: adminTokens.colors.text }}>{card.value}</Typography>
                                    <Typography sx={{ color: adminTokens.colors.muted, fontSize: "0.86rem" }}>{card.detail}</Typography>
                                </Box>
                                <Box sx={{ display: "grid", placeItems: "center", width: 48, height: 48, borderRadius: 3.2, color: card.tone, bgcolor: `${card.tone}16` }}>
                                    {card.icon}
                                </Box>
                            </Box>
                        </SurfaceCard>
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
                <Grid item xs={12} lg={8}>
                    <SurfaceCard>
                        <SectionHeader
                            title={t('real_time_driver_section_text')}
                            description={`${locations.length} tracked driver location${locations.length === 1 ? "" : "s"} from the current dashboard feed.`}
                            actions={<StatusBadge tone="success">Live map</StatusBadge>}
                        />
                        <Box sx={{ overflow: "hidden", borderRadius: 4, border: `1px solid ${adminTokens.colors.border}` }}>
                            {mylocation && mylocation.lat ? (
                                <GoogleMap
                                    zoom={10}
                                    center={mylocation}
                                    mapContainerStyle={{height: `430px`, minHeight: `430px`, width: '100%', fontFamily: FONT_FAMILY }}
                                >
                                    {locations.map(marker => (
                                        <Marker
                                            position={{ lat: marker.lat, lng: marker.lng }}
                                            key={marker.id}
                                            icon={{
                                                url: marker.carImage,
                                                scaledSize: new window.google.maps.Size(35,25)
                                            }}
                                        >
                                            <InfoWindow
                                                position={{ lat: marker.lat, lng: marker.lng }}
                                                options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
                                            >
                                                <div style={{ fontFamily: FONT_FAMILY }}>
                                                    {marker.drivername}<br/>{marker.carnumber}{marker.bookingRef ? <><br/>Booking: {marker.bookingRef}</> : null}
                                                </div>
                                            </InfoWindow>
                                        </Marker>
                                    ))}
                                </GoogleMap>
                            ) : (
                                <EmptyState title="Location not available" description="The driver map will appear when browser location is available." />
                            )}
                        </Box>
                    </SurfaceCard>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <SurfaceCard sx={{ mb: 2.5 }}>
                        <SectionHeader title="Booking Status" description="Real counts from loaded bookings." />
                        <Box sx={{ display: "grid", gap: 1.8 }}>
                            {statusOverview.map((status) => (
                                <Box key={status.label}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8, gap: 1 }}>
                                        <Typography sx={{ fontWeight: 850, color: adminTokens.colors.text }}>{status.label}</Typography>
                                        <Typography sx={{ fontWeight: 900, color: status.tone }}>{status.value}</Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={status.percent}
                                        aria-label={`${status.label} bookings ${status.value}`}
                                        sx={{
                                            height: 10,
                                            borderRadius: 999,
                                            bgcolor: `${status.tone}16`,
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: 999,
                                                bgcolor: status.tone
                                            }
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </SurfaceCard>

                    <SurfaceCard>
                        <SectionHeader title="Operations Summary" description="Compact view of current operational queues." />
                        <Box sx={{ display: "grid", gap: 1.2 }}>
                            {[
                                { label: "Active Deliveries", value: dashboardMetrics.activeBookings, route: "/bookings", icon: <LocalShippingIcon />, tone: adminTokens.colors.garahexOrange },
                                { label: "Empty Container Returns", value: dashboardMetrics.ecrBookings, route: "/empty-container-returns", icon: <AssignmentReturnIcon />, tone: adminTokens.colors.cyan },
                                { label: "Bid Monitoring", value: dashboardMetrics.pendingBids, route: "/admin-bid-monitoring", icon: <AssessmentIcon />, tone: adminTokens.colors.purple }
                            ].map((item) => (
                                <Button
                                    key={item.label}
                                    onClick={() => navigate(item.route)}
                                    sx={{
                                        justifyContent: "space-between",
                                        p: 1.5,
                                        borderRadius: 3,
                                        color: adminTokens.colors.text,
                                        bgcolor: `${item.tone}10`,
                                        textTransform: "none",
                                        "&:hover": { bgcolor: `${item.tone}18` }
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                        <Box sx={{ display: "grid", placeItems: "center", color: item.tone }}>{item.icon}</Box>
                                        <Typography sx={{ fontWeight: 850 }}>{item.label}</Typography>
                                    </Box>
                                    <Chip label={item.value} size="small" sx={{ fontWeight: 900, bgcolor: "#fff" }} />
                                </Button>
                            ))}
                        </Box>
                    </SurfaceCard>
                </Grid>
            </Grid>

            <Grid container spacing={2.5}>
                <Grid item xs={12} xl={7}>
                    <SurfaceCard>
                        <SectionHeader
                            title="Recent Bookings"
                            description="Latest bookings from the data already loaded on this dashboard."
                            actions={<Button variant="text" onClick={() => navigate("/bookings")} sx={{ fontWeight: 850, textTransform: "none" }}>View All Bookings</Button>}
                        />
                        {recentBookings.length > 0 ? (
                            <Box sx={{ display: "grid", gap: 1 }}>
                                {recentBookings.map((booking) => {
                                    const status = normalizeStatus(booking.status);
                                    const date = getBookingDate(booking);
                                    return (
                                        <Box
                                            key={getBookingReference(booking)}
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", md: "1.1fr 1.6fr 1fr auto" },
                                                gap: 1.5,
                                                alignItems: "center",
                                                p: 1.5,
                                                borderRadius: 3,
                                                border: `1px solid ${adminTokens.colors.border}`,
                                                bgcolor: "rgba(244,248,252,0.66)"
                                            }}
                                        >
                                            <Box>
                                                <Typography sx={{ fontWeight: 900, color: adminTokens.colors.text }}>{getBookingReference(booking)}</Typography>
                                                <Typography sx={{ color: adminTokens.colors.muted, fontSize: "0.82rem" }}>{getBookingClient(booking)}</Typography>
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography noWrap sx={{ fontWeight: 800, color: adminTokens.colors.text }}>{getBookingPickup(booking)}</Typography>
                                                <Typography noWrap sx={{ color: adminTokens.colors.muted, fontSize: "0.82rem" }}>{getBookingDrop(booking)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontWeight: 800 }}>{getShipmentType(booking)}</Typography>
                                                <Typography sx={{ color: adminTokens.colors.muted, fontSize: "0.82rem" }}>{date ? formatDate(date) : "-"}</Typography>
                                            </Box>
                                            <AdminStatusBadge status={status} />
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            <EmptyState title="No recent bookings" description="Recent bookings will appear here once they are available in the current dashboard data." />
                        )}
                    </SurfaceCard>
                </Grid>

                <Grid item xs={12} xl={5}>
                    <SurfaceCard sx={{ mb: 2.5 }}>
                        <SectionHeader title="Needs Attention" description="Only real pending conditions from loaded dashboard data." />
                        {needsAttention.length > 0 ? (
                            <Box sx={{ display: "grid", gap: 1.2 }}>
                                {needsAttention.map((item) => (
                                    <Button
                                        key={item.label}
                                        onClick={() => navigate(item.route)}
                                        sx={{
                                            justifyContent: "space-between",
                                            p: 1.5,
                                            borderRadius: 3,
                                            bgcolor: "rgba(245,165,36,0.1)",
                                            color: adminTokens.colors.text,
                                            textTransform: "none"
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 850 }}>{item.label}</Typography>
                                        <StatusBadge tone={item.tone}>{item.value}</StatusBadge>
                                    </Button>
                                ))}
                            </Box>
                        ) : (
                            <EmptyState title="No pending dashboard alerts" description="There are no supported attention items in the current loaded data." />
                        )}
                    </SurfaceCard>

                    <SurfaceCard>
                        <SectionHeader title={t('booking_chart')} description="Completed and cancelled bookings by month." />
                        <Chart
                            options={barChartOptions}
                            series={barChart}
                            type="area"
                            width="100%"
                            height={320}
                        />
                    </SurfaceCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;
